import { useEffect, useRef, useState } from "react";
import { getAdminHeaders, toAssetUrl } from "./adminApi";

const TABS = [
  { key: "products", label: "商品" },
  { key: "categories", label: "分类" },
  { key: "orders", label: "订单" },
  { key: "suppliers", label: "供应商" },
];

const emptyProduct = {
  name: "",
  slug: "",
  subtitle: "",
  description: "",
  category_id: "",
  main_image: "",
  price: "0.00",
  compare_at_price: "",
  cost_price: "",
  stock_mode: "unlimited",
  stock_quantity: 0,
  sku: "",
  specifications: "",
  brand: "",
  manufacturer: "",
  origin: "",
  product_license: "",
  shipping_note: "运费与发货安排以平台确认信息为准。",
  after_sale_note: "",
  status: "draft",
  is_featured: 0,
  seo_title: "",
  seo_description: "",
  sort_order: 0,
};

const emptyCategory = {
  name: "",
  slug: "",
  description: "",
  sort_order: 0,
  is_active: 1,
};

const emptySupplier = {
  name: "",
  contact_name: "",
  contact_phone: "",
  shipping_notes: "",
  settlement_notes: "",
  status: 1,
};

async function shopAdmin(path, options = {}) {
  const withJson = options.body && !(options.body instanceof FormData);
  const res = await fetch(`/api/shop/v1/admin${path}`, {
    ...options,
    headers: {
      ...getAdminHeaders(withJson),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    localStorage.removeItem("admin_token");
    window.location.href = "/admin/login";
    throw new Error("管理员未登录");
  }
  if (!res.ok) {
    throw new Error(typeof data.detail === "string" ? data.detail : "请求失败");
  }
  return data;
}

export default function AdminShop() {
  const [tab, setTab] = useState("products");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [orderKeyword, setOrderKeyword] = useState("");
  const [orderStatus, setOrderStatus] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState(emptyProduct);
  const [catForm, setCatForm] = useState(emptyCategory);
  const [editingCat, setEditingCat] = useState(null);
  const [supForm, setSupForm] = useState(emptySupplier);
  const [editingSup, setEditingSup] = useState(null);
  const [shipForm, setShipForm] = useState({
    tracking_company: "",
    tracking_no: "",
    note: "",
    public_note: "",
  });
  const fileRef = useRef(null);

  const flash = (msg, isErr = false) => {
    setMessage(isErr ? "" : msg);
    setError(isErr ? msg : "");
  };

  const loadProducts = async () => {
    const data = await shopAdmin("/products?page_size=100");
    setProducts(data.items || []);
  };
  const loadCategories = async () => {
    const data = await shopAdmin("/categories");
    setCategories(data.items || data || []);
  };
  const loadOrders = async () => {
    const q = new URLSearchParams({ page_size: "50" });
    if (orderStatus) q.set("status", orderStatus);
    if (orderKeyword.trim()) q.set("keyword", orderKeyword.trim());
    const data = await shopAdmin(`/orders?${q}`);
    setOrders(data.items || []);
  };
  const loadSuppliers = async () => {
    const data = await shopAdmin("/suppliers");
    setSuppliers(data.items || []);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      if (tab === "products") {
        await Promise.all([loadProducts(), loadCategories()]);
      } else if (tab === "categories") {
        await loadCategories();
      } else if (tab === "orders") {
        await loadOrders();
      } else if (tab === "suppliers") {
        await loadSuppliers();
      }
    } catch (e) {
      flash(e.message || "加载失败", true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, orderStatus]);

  const openCreateProduct = () => {
    setEditingProduct(null);
    setProductForm({
      ...emptyProduct,
      category_id: categories[0]?.id || "",
    });
  };

  const openEditProduct = (p) => {
    setEditingProduct(p);
    setProductForm({
      ...emptyProduct,
      ...p,
      category_id: p.category_id || "",
      price: String(p.price ?? "0.00"),
      compare_at_price: p.compare_at_price != null ? String(p.compare_at_price) : "",
      cost_price: p.cost_price != null ? String(p.cost_price) : "",
      is_featured: p.is_featured ? 1 : 0,
      specifications:
        typeof p.specifications === "string"
          ? p.specifications
          : p.specifications
            ? JSON.stringify(p.specifications)
            : "",
    });
  };

  const saveProduct = async () => {
    try {
      const body = {
        ...productForm,
        category_id: productForm.category_id ? Number(productForm.category_id) : null,
        price: String(productForm.price || "0"),
        compare_at_price: productForm.compare_at_price
          ? String(productForm.compare_at_price)
          : null,
        cost_price: productForm.cost_price ? String(productForm.cost_price) : null,
        stock_quantity: Number(productForm.stock_quantity) || 0,
        is_featured: productForm.is_featured ? 1 : 0,
        sort_order: Number(productForm.sort_order) || 0,
      };
      if (editingProduct) {
        await shopAdmin(`/products/${editingProduct.id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
        flash("商品已更新");
      } else {
        await shopAdmin("/products", {
          method: "POST",
          body: JSON.stringify(body),
        });
        flash("商品已创建");
      }
      setEditingProduct(null);
      setProductForm(emptyProduct);
      await loadProducts();
    } catch (e) {
      flash(e.message, true);
    }
  };

  const setProductStatus = async (id, action) => {
    try {
      await shopAdmin(`/products/${id}/${action}`, { method: "POST", body: "{}" });
      flash(action === "publish" ? "已上架" : "已下架");
      await loadProducts();
    } catch (e) {
      flash(e.message, true);
    }
  };

  const uploadMainImage = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const data = await shopAdmin("/upload-image", { method: "POST", body: form });
      setProductForm((f) => ({ ...f, main_image: data.url }));
      flash("图片已上传");
    } catch (e) {
      flash(e.message, true);
    }
  };

  const saveCategory = async () => {
    try {
      const body = {
        ...catForm,
        sort_order: Number(catForm.sort_order) || 0,
        is_active: catForm.is_active ? 1 : 0,
      };
      if (editingCat) {
        await shopAdmin(`/categories/${editingCat.id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
        flash("分类已更新");
      } else {
        await shopAdmin("/categories", {
          method: "POST",
          body: JSON.stringify(body),
        });
        flash("分类已创建");
      }
      setEditingCat(null);
      setCatForm(emptyCategory);
      await loadCategories();
    } catch (e) {
      flash(e.message, true);
    }
  };

  const deactivateCategory = async (id) => {
    try {
      await shopAdmin(`/categories/${id}`, { method: "DELETE" });
      flash("分类已停用");
      await loadCategories();
    } catch (e) {
      flash(e.message, true);
    }
  };

  const openOrder = async (orderNo) => {
    try {
      const data = await shopAdmin(`/orders/${encodeURIComponent(orderNo)}`);
      setSelectedOrder(data);
      setShipForm({
        tracking_company: data.tracking_company || "",
        tracking_no: data.tracking_no || "",
        note: "",
        public_note: data.public_admin_note || "",
      });
    } catch (e) {
      flash(e.message, true);
    }
  };

  const orderAction = async (action) => {
    if (!selectedOrder) return;
    try {
      const data = await shopAdmin(
        `/orders/${encodeURIComponent(selectedOrder.order_no)}/${action}`,
        {
          method: "POST",
          body: JSON.stringify(shipForm),
        }
      );
      setSelectedOrder(data.order || data);
      flash("订单已更新");
      await loadOrders();
    } catch (e) {
      flash(e.message, true);
    }
  };

  const saveSupplier = async () => {
    try {
      const body = { ...supForm, status: supForm.status ? 1 : 0 };
      if (editingSup) {
        await shopAdmin(`/suppliers/${editingSup.id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
        flash("供应商已更新");
      } else {
        await shopAdmin("/suppliers", {
          method: "POST",
          body: JSON.stringify(body),
        });
        flash("供应商已创建");
      }
      setEditingSup(null);
      setSupForm(emptySupplier);
      await loadSuppliers();
    } catch (e) {
      flash(e.message, true);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">生活好物商城</h2>
          <p className="text-sm text-gray-500 mt-1">
            人工确认订单，暂不在线支付。内部成本仅管理员可见。
          </p>
        </div>
        <a
          href="/shop"
          target="_blank"
          rel="noreferrer"
          className="px-4 py-2 rounded-xl bg-pink-50 text-pink-600 text-sm font-medium"
        >
          打开前台商城
        </a>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-xl text-sm font-medium ${
              tab === t.key ? "bg-blue-600 text-white" : "bg-white text-gray-600 border"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {message ? (
        <div className="rounded-xl bg-emerald-50 text-emerald-700 px-4 py-2 text-sm">{message}</div>
      ) : null}
      {error ? (
        <div className="rounded-xl bg-rose-50 text-rose-700 px-4 py-2 text-sm">{error}</div>
      ) : null}
      {loading ? <p className="text-sm text-gray-400">加载中…</p> : null}

      {tab === "products" ? (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
            <div className="flex justify-between items-center">
              <h3 className="font-bold text-gray-800">商品列表</h3>
              <button
                type="button"
                onClick={openCreateProduct}
                className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm"
              >
                新增
              </button>
            </div>
            <div className="space-y-2 max-h-[70vh] overflow-y-auto">
              {products.map((p) => (
                <div
                  key={p.id}
                  className="flex gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50"
                >
                  <img
                    src={toAssetUrl(p.main_image) || "https://via.placeholder.com/64"}
                    alt=""
                    className="w-14 h-14 rounded-lg object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{p.name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      ¥{p.price} · {p.status} {p.is_featured ? "· 精选" : ""}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <button
                        type="button"
                        className="text-xs text-blue-600"
                        onClick={() => openEditProduct(p)}
                      >
                        编辑
                      </button>
                      {p.status !== "published" ? (
                        <button
                          type="button"
                          className="text-xs text-emerald-600"
                          onClick={() => setProductStatus(p.id, "publish")}
                        >
                          上架
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="text-xs text-amber-600"
                          onClick={() => setProductStatus(p.id, "archive")}
                        >
                          下架
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {!products.length ? (
                <p className="text-sm text-gray-400 py-6 text-center">暂无商品，请先新增并上架</p>
              ) : null}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
            <h3 className="font-bold text-gray-800">
              {editingProduct ? `编辑商品 #${editingProduct.id}` : "新增商品"}
            </h3>
            <div className="grid gap-2 text-sm">
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="商品名称 *"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
              />
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="副标题"
                value={productForm.subtitle}
                onChange={(e) => setProductForm({ ...productForm, subtitle: e.target.value })}
              />
              <select
                className="border rounded-lg px-3 py-2"
                value={productForm.category_id}
                onChange={(e) =>
                  setProductForm({ ...productForm, category_id: e.target.value })
                }
              >
                <option value="">选择分类</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <div className="grid grid-cols-2 gap-2">
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder="售价 *"
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                />
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder="参考价"
                  value={productForm.compare_at_price}
                  onChange={(e) =>
                    setProductForm({ ...productForm, compare_at_price: e.target.value })
                  }
                />
              </div>
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="内部采购成本（仅管理员）"
                value={productForm.cost_price}
                onChange={(e) => setProductForm({ ...productForm, cost_price: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-2">
                <select
                  className="border rounded-lg px-3 py-2"
                  value={productForm.stock_mode}
                  onChange={(e) =>
                    setProductForm({ ...productForm, stock_mode: e.target.value })
                  }
                >
                  <option value="unlimited">不限库存</option>
                  <option value="tracked">跟踪库存</option>
                </select>
                <input
                  type="number"
                  className="border rounded-lg px-3 py-2"
                  placeholder="库存数量"
                  value={productForm.stock_quantity}
                  onChange={(e) =>
                    setProductForm({ ...productForm, stock_quantity: e.target.value })
                  }
                />
              </div>
              <textarea
                className="border rounded-lg px-3 py-2"
                rows={4}
                placeholder="商品详情"
                value={productForm.description}
                onChange={(e) =>
                  setProductForm({ ...productForm, description: e.target.value })
                }
              />
              <textarea
                className="border rounded-lg px-3 py-2"
                rows={2}
                placeholder="规格说明"
                value={productForm.specifications}
                onChange={(e) =>
                  setProductForm({ ...productForm, specifications: e.target.value })
                }
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder="品牌"
                  value={productForm.brand}
                  onChange={(e) => setProductForm({ ...productForm, brand: e.target.value })}
                />
                <input
                  className="border rounded-lg px-3 py-2"
                  placeholder="SKU"
                  value={productForm.sku}
                  onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                />
              </div>
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="生产企业"
                value={productForm.manufacturer}
                onChange={(e) =>
                  setProductForm({ ...productForm, manufacturer: e.target.value })
                }
              />
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="产地"
                value={productForm.origin}
                onChange={(e) => setProductForm({ ...productForm, origin: e.target.value })}
              />
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="资质/备案信息"
                value={productForm.product_license}
                onChange={(e) =>
                  setProductForm({ ...productForm, product_license: e.target.value })
                }
              />
              <textarea
                className="border rounded-lg px-3 py-2"
                rows={2}
                placeholder="发货说明"
                value={productForm.shipping_note}
                onChange={(e) =>
                  setProductForm({ ...productForm, shipping_note: e.target.value })
                }
              />
              <textarea
                className="border rounded-lg px-3 py-2"
                rows={2}
                placeholder="售后说明"
                value={productForm.after_sale_note}
                onChange={(e) =>
                  setProductForm({ ...productForm, after_sale_note: e.target.value })
                }
              />
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="SEO 标题"
                value={productForm.seo_title}
                onChange={(e) => setProductForm({ ...productForm, seo_title: e.target.value })}
              />
              <input
                className="border rounded-lg px-3 py-2"
                placeholder="SEO 描述"
                value={productForm.seo_description}
                onChange={(e) =>
                  setProductForm({ ...productForm, seo_description: e.target.value })
                }
              />
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={!!productForm.is_featured}
                  onChange={(e) =>
                    setProductForm({ ...productForm, is_featured: e.target.checked ? 1 : 0 })
                  }
                />
                精选商品
              </label>
              <div className="flex items-center gap-3">
                {productForm.main_image ? (
                  <img
                    src={toAssetUrl(productForm.main_image)}
                    alt=""
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                ) : null}
                <button
                  type="button"
                  className="px-3 py-2 rounded-lg border text-sm"
                  onClick={() => fileRef.current?.click()}
                >
                  上传主图
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => uploadMainImage(e.target.files?.[0])}
                />
              </div>
              <button
                type="button"
                onClick={saveProduct}
                className="mt-2 py-2.5 rounded-xl bg-blue-600 text-white font-medium"
              >
                保存商品
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {tab === "categories" ? (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
            <h3 className="font-bold">分类列表</h3>
            {categories.map((c) => (
              <div key={c.id} className="flex justify-between items-center border-b py-2 text-sm">
                <div>
                  <p className="font-medium">{c.name}</p>
                  <p className="text-xs text-gray-400">
                    {c.slug} · {c.is_active ? "启用" : "停用"}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="text-blue-600"
                    onClick={() => {
                      setEditingCat(c);
                      setCatForm({
                        name: c.name,
                        slug: c.slug,
                        description: c.description || "",
                        sort_order: c.sort_order || 0,
                        is_active: c.is_active ? 1 : 0,
                      });
                    }}
                  >
                    编辑
                  </button>
                  <button
                    type="button"
                    className="text-amber-600"
                    onClick={() => deactivateCategory(c.id)}
                  >
                    停用
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
            <h3 className="font-bold">{editingCat ? "编辑分类" : "新增分类"}</h3>
            <input
              className="border rounded-lg px-3 py-2 w-full text-sm"
              placeholder="名称"
              value={catForm.name}
              onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
            />
            <input
              className="border rounded-lg px-3 py-2 w-full text-sm"
              placeholder="slug（可空，自动生成）"
              value={catForm.slug}
              onChange={(e) => setCatForm({ ...catForm, slug: e.target.value })}
            />
            <textarea
              className="border rounded-lg px-3 py-2 w-full text-sm"
              rows={3}
              placeholder="介绍"
              value={catForm.description}
              onChange={(e) => setCatForm({ ...catForm, description: e.target.value })}
            />
            <input
              type="number"
              className="border rounded-lg px-3 py-2 w-full text-sm"
              placeholder="排序"
              value={catForm.sort_order}
              onChange={(e) => setCatForm({ ...catForm, sort_order: e.target.value })}
            />
            <button
              type="button"
              onClick={saveCategory}
              className="w-full py-2.5 rounded-xl bg-blue-600 text-white"
            >
              保存分类
            </button>
          </div>
        </div>
      ) : null}

      {tab === "orders" ? (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
            <div className="flex flex-wrap gap-2">
              <input
                className="border rounded-lg px-3 py-2 text-sm flex-1 min-w-[140px]"
                placeholder="订单号/姓名/电话"
                value={orderKeyword}
                onChange={(e) => setOrderKeyword(e.target.value)}
              />
              <select
                className="border rounded-lg px-3 py-2 text-sm"
                value={orderStatus}
                onChange={(e) => setOrderStatus(e.target.value)}
              >
                <option value="">全部状态</option>
                <option value="pending_confirmation">待确认</option>
                <option value="confirmed">已确认</option>
                <option value="preparing_shipment">备货中</option>
                <option value="shipped">已发货</option>
                <option value="completed">已完成</option>
                <option value="cancelled">已取消</option>
              </select>
              <button
                type="button"
                onClick={loadOrders}
                className="px-3 py-2 rounded-lg bg-gray-800 text-white text-sm"
              >
                搜索
              </button>
            </div>
            <div className="space-y-2 max-h-[70vh] overflow-y-auto">
              {orders.map((o) => (
                <button
                  key={o.order_no}
                  type="button"
                  onClick={() => openOrder(o.order_no)}
                  className="w-full text-left p-3 rounded-xl border hover:bg-gray-50"
                >
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{o.order_no}</span>
                    <span className="text-pink-600">{o.status_label}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {o.recipient_name} · ¥{o.total_amount}
                  </p>
                </button>
              ))}
              {!orders.length ? (
                <p className="text-sm text-gray-400 text-center py-8">暂无订单</p>
              ) : null}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
            {!selectedOrder ? (
              <p className="text-sm text-gray-400 py-10 text-center">选择左侧订单查看详情</p>
            ) : (
              <>
                <h3 className="font-bold text-gray-800">订单 {selectedOrder.order_no}</h3>
                <p className="text-sm text-pink-600">{selectedOrder.status_label}</p>
                <p className="text-sm text-gray-600">
                  用户 ID：{selectedOrder.user_id}
                  <br />
                  {selectedOrder.recipient_name} {selectedOrder.recipient_phone}
                  <br />
                  {selectedOrder.shipping_address}
                </p>
                <div className="text-sm space-y-1 border-t pt-3">
                  {(selectedOrder.items || []).map((it) => (
                    <p key={it.id}>
                      {it.product_name} × {it.quantity} = ¥{it.line_total}
                    </p>
                  ))}
                  <p className="font-semibold">合计 ¥{selectedOrder.total_amount}</p>
                </div>
                <p className="text-xs text-gray-400">支付状态：{selectedOrder.payment_status}</p>
                <textarea
                  className="border rounded-lg px-3 py-2 w-full text-sm"
                  rows={2}
                  placeholder="内部备注"
                  value={shipForm.note}
                  onChange={(e) => setShipForm({ ...shipForm, note: e.target.value })}
                />
                <textarea
                  className="border rounded-lg px-3 py-2 w-full text-sm"
                  rows={2}
                  placeholder="对用户可见说明"
                  value={shipForm.public_note}
                  onChange={(e) => setShipForm({ ...shipForm, public_note: e.target.value })}
                />
                <div className="grid grid-cols-2 gap-2">
                  <input
                    className="border rounded-lg px-3 py-2 text-sm"
                    placeholder="物流公司"
                    value={shipForm.tracking_company}
                    onChange={(e) =>
                      setShipForm({ ...shipForm, tracking_company: e.target.value })
                    }
                  />
                  <input
                    className="border rounded-lg px-3 py-2 text-sm"
                    placeholder="物流单号"
                    value={shipForm.tracking_no}
                    onChange={(e) => setShipForm({ ...shipForm, tracking_no: e.target.value })}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="px-3 py-2 rounded-lg bg-blue-600 text-white text-sm"
                    onClick={() => orderAction("confirm")}
                  >
                    确认
                  </button>
                  <button
                    type="button"
                    className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm"
                    onClick={() => orderAction("prepare")}
                  >
                    备货中
                  </button>
                  <button
                    type="button"
                    className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm"
                    onClick={() => orderAction("shipment")}
                  >
                    发货
                  </button>
                  <button
                    type="button"
                    className="px-3 py-2 rounded-lg bg-gray-700 text-white text-sm"
                    onClick={() => orderAction("complete")}
                  >
                    完成
                  </button>
                  <button
                    type="button"
                    className="px-3 py-2 rounded-lg border border-rose-300 text-rose-600 text-sm"
                    onClick={() => orderAction("cancel")}
                  >
                    取消
                  </button>
                </div>
                {(selectedOrder.logs || []).length ? (
                  <div className="border-t pt-3 space-y-1">
                    <p className="text-sm font-semibold">操作日志</p>
                    {selectedOrder.logs.map((log) => (
                      <p key={log.id} className="text-xs text-gray-500">
                        [{log.created_at}] {log.action}: {log.old_status} → {log.new_status}{" "}
                        {log.note}
                      </p>
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      ) : null}

      {tab === "suppliers" ? (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
            <h3 className="font-bold">供应商</h3>
            {suppliers.map((s) => (
              <div key={s.id} className="border-b py-2 text-sm flex justify-between">
                <div>
                  <p className="font-medium">{s.name}</p>
                  <p className="text-xs text-gray-400">
                    {s.contact_name} {s.contact_phone}
                  </p>
                </div>
                <button
                  type="button"
                  className="text-blue-600"
                  onClick={() => {
                    setEditingSup(s);
                    setSupForm({
                      name: s.name,
                      contact_name: s.contact_name || "",
                      contact_phone: s.contact_phone || "",
                      shipping_notes: s.shipping_notes || "",
                      settlement_notes: s.settlement_notes || "",
                      status: s.status ? 1 : 0,
                    });
                  }}
                >
                  编辑
                </button>
              </div>
            ))}
            {!suppliers.length ? (
              <p className="text-sm text-gray-400 text-center py-6">暂无供应商</p>
            ) : null}
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
            <h3 className="font-bold">{editingSup ? "编辑供应商" : "新增供应商"}</h3>
            {[
              ["name", "名称"],
              ["contact_name", "联系人"],
              ["contact_phone", "电话"],
            ].map(([k, label]) => (
              <input
                key={k}
                className="border rounded-lg px-3 py-2 w-full text-sm"
                placeholder={label}
                value={supForm[k]}
                onChange={(e) => setSupForm({ ...supForm, [k]: e.target.value })}
              />
            ))}
            <textarea
              className="border rounded-lg px-3 py-2 w-full text-sm"
              rows={2}
              placeholder="发货说明"
              value={supForm.shipping_notes}
              onChange={(e) => setSupForm({ ...supForm, shipping_notes: e.target.value })}
            />
            <textarea
              className="border rounded-lg px-3 py-2 w-full text-sm"
              rows={2}
              placeholder="结算约定（内部）"
              value={supForm.settlement_notes}
              onChange={(e) => setSupForm({ ...supForm, settlement_notes: e.target.value })}
            />
            <button
              type="button"
              onClick={saveSupplier}
              className="w-full py-2.5 rounded-xl bg-blue-600 text-white"
            >
              保存供应商
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
